import React, { useState, useEffect, useCallback } from 'react';
import { Search as SearchIcon, Trash2, Download, Upload } from 'lucide-react';
import { getAdminSubscribers, deleteAdminSubscriber } from '../../lib/api';
import { patchSubscriber, getSubscriberFilters } from '../../lib/campaignsApi';
import { Toaster, toast } from 'sonner';
import { SubscriberImportModal } from './SubscriberImportModal';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const AdminSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [segments, setSegments] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      const res = await getAdminSubscribers(params);
      setSubscribers(res.data);
    } catch (err) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  useEffect(() => {
    getSubscriberFilters().then((r) => setSegments(r.data.segments || [])).catch(() => {});
  }, []);

  const handleSegmentChange = async (subId, newSegment) => {
    try {
      await patchSubscriber(subId, { segment: newSegment });
      setSubscribers((prev) => prev.map((s) => (s.id === subId ? { ...s, segment: newSegment || null } : s)));
      toast.success('Segment updated');
    } catch (e) {
      toast.error('Failed to update segment');
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Remove subscriber ${email}?`)) return;
    try {
      await deleteAdminSubscriber(id);
      toast.success('Subscriber removed');
      fetchSubscribers();
    } catch (err) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleExport = () => {
    if (subscribers.length === 0) {
      toast.info('No subscribers to export');
      return;
    }
    const headers = ['Name', 'Company', 'Country', 'Segment', 'Email', 'WhatsApp', 'Joined', 'Last Opened', 'Last Clicked'];
    const rows = subscribers.map((s) => [
      s.name, s.company || '', s.country, s.segment || '', s.email, s.whatsapp || '',
      formatDate(s.created_at), formatDate(s.last_opened_at), formatDate(s.last_clicked_at),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `luxortek-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6" data-testid="admin-subscribers-page">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="subscribers-title">
            Weekly Inventory Subscribers
          </h1>
          <p className="text-white/55 text-sm">
            {subscribers.length} active subscriber{subscribers.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex gap-2 self-start flex-wrap">
          <button
            onClick={() => setShowImport(true)}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="import-subscribers-btn"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </button>
          <button
            onClick={handleExport}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="export-subscribers-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <input
          type="text"
          placeholder="Search by name, email, company, country..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
          data-testid="subscribers-search"
        />
      </div>

      {loading ? (
        <p className="text-white/50 text-sm">Loading subscribers...</p>
      ) : subscribers.length === 0 ? (
        <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center" data-testid="subscribers-empty">
          <p className="text-white/55 text-sm">No subscribers yet.</p>
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead className="bg-[#0A0A0A] text-white/55 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Country</th>
                <th className="text-left px-4 py-3">Segment</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-left px-4 py-3">Last Opened</th>
                <th className="text-left px-4 py-3">Last Clicked</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                  data-testid={`subscriber-row-${s.id}`}
                >
                  <td className="px-4 py-3 text-white">{s.name}</td>
                  <td className="px-4 py-3 text-white/65">{s.company || '\u2014'}</td>
                  <td className="px-4 py-3 text-white/65">{s.country}</td>
                  <td className="px-4 py-3">
                    <select
                      value={s.segment || ''}
                      onChange={(e) => handleSegmentChange(s.id, e.target.value)}
                      className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-1 focus:border-[#00D4FF] outline-none"
                      data-testid={`subscriber-segment-${s.id}`}
                    >
                      <option value="">—</option>
                      {segments.map((seg) => <option key={seg} value={seg}>{seg}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-[#00D4FF]">{s.email}</td>
                  <td className="px-4 py-3 text-white/50 text-xs">{formatDate(s.created_at)}</td>
                  <td className="px-4 py-3 text-white/50 text-xs">{formatDate(s.last_opened_at)}</td>
                  <td className="px-4 py-3 text-white/50 text-xs">{formatDate(s.last_clicked_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(s.id, s.email)}
                      className="text-white/40 hover:text-red-400 transition-colors"
                      data-testid={`delete-subscriber-${s.id}`}
                      title="Remove subscriber"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showImport && (
        <SubscriberImportModal
          onClose={() => setShowImport(false)}
          onImported={() => fetchSubscribers()}
        />
      )}
    </div>
  );
};
